import type { FreeColumn, FreeRow } from '@/components/table/FreeSheetTable'
import type { Supplier } from '@/services/suppliers'
import type { PoLine, PurchaseOrder } from '@/services/procurement'
import { parseNumberVi } from '@/components/table/freeSheetModel'

type Draft = {
  poNumber: string
  supplier: Supplier | null
  supplierName: string
  supplierAddress: string
  supplierContact: string
  supplierPhone: string
  workshop: string
  deliveryDate: string
  notes: string
  items: Array<{ id: string | number; name: string; spec?: string; uom: string; qty: number | string; price: number | null; lineTotal: number; notes: string; material_id?: string; vat?: number | null; priceSource?: string }>
  vatRate: number
  subtotal: number
  vatTotal: number
  grandTotal: number
  isFree: boolean
  freeColumns: FreeColumn[]
  freeRows: FreeRow[]
  signatures: Array<{ title: string; name: string }>
}

export function mapPoDraftToPrintModel(draft: Draft): { po: PurchaseOrder; supplier: Supplier | null } {
  const numberOf = (value: unknown, fallback = 0) => {
    const parsed = typeof value === 'number' ? value : parseNumberVi(value)
    return parsed != null && Number.isFinite(parsed) ? parsed : fallback
  }
  const supplier = draft.supplier
    ? { ...draft.supplier, name: draft.supplierName || draft.supplier.name, address: draft.supplierAddress || draft.supplier.address, contact_person: draft.supplierContact || draft.supplier.contact_person, phone: draft.supplierPhone || draft.supplier.phone }
    : draft.supplierName || draft.supplierAddress || draft.supplierContact || draft.supplierPhone
      ? { id: '', code: '', name: draft.supplierName, category_ids: [], address: draft.supplierAddress, contact_person: draft.supplierContact, phone: draft.supplierPhone, default_vat_rate: draft.vatRate, payment_term_id: '', lead_time_days: 0, status: 'ACTIVE' as const, created_at: '', updated_at: '' }
      : null
  const sourceItems = draft.isFree && draft.freeRows.length
    ? draft.freeRows.map((row, index) => ({
      id: row.id || `free-line-${index + 1}`, name: String(row.name ?? ''), spec: '',
      uom: String(row.uom ?? ''), qty: row.qty ?? 0,
      price: row.unit_price == null ? null : numberOf(row.unit_price),
      lineTotal: row.line_total ?? 0, notes: String(row.note ?? ''),
      material_id: undefined, vat: undefined, priceSource: undefined,
    }))
    : draft.items
  const lines: PoLine[] = sourceItems.map((item, index) => ({
    id: String(item.id || `draft-line-${index + 1}`), materialId: item.material_id || '', itemCode: '', itemName: item.name,
    spec: item.spec || '', uom: item.uom, qty: Number(item.qty) || 0, receivedQty: 0, shortQty: 0,
    unitPriceSystem: numberOf(item.price), unitPriceActual: numberOf(item.price),
    vatRateStatus: 'KNOWN', vat: item.vat ?? draft.vatRate, currency: 'VND', priceSource: item.priceSource || 'MANUAL',
    lineTotal: numberOf(item.lineTotal), manualReason: item.notes,
  }))
  return {
    supplier,
    po: {
      id: 'draft', code: draft.poNumber, supplierId: draft.supplier?.id || '', supplierName: draft.supplierName,
      status: 'DRAFT' as PurchaseOrder['status'], createdAt: new Date().toISOString().slice(0, 10), deliveryDate: draft.deliveryDate,
      deliverySite: draft.workshop, currency: 'VND', lines, version: 0, note: draft.notes, kind: draft.isFree ? 'FREE' : 'STANDARD',
      freeLayout: draft.freeColumns, freeLines: draft.freeRows, vat_rate: draft.vatRate, vatTotal: draft.vatTotal, grandTotal: draft.grandTotal,
      signers: draft.signatures,
    },
  }
}
